import React from 'react';
import { t } from 'Src/i18n';
import { Formik } from 'formik';
import { BucketFormikForm } from 'Src/types/forms';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { fetchAdditionalTransports } from 'Store/booking/additional-transports/additionalTransports.action';
import { getTransportSearchCriteria } from 'Store/booking/proposals/proposals.action';
import { useAppContext } from 'Context/ReactAppContext';
import { COLORS } from 'Components/utils/color/colors';
import { Button } from 'Components/frameworks/button/Button';
import { queryParamAsString } from 'Src/types/view-helpers';
import { AdditionalTransportAdvancedSearchFormFields } from './AdditionalTransportAdvancedSearchFormFields';
type AdditionalTransportAdvancedSearchFormProps = {
  proposal: ProposalWithTransport;
};
type TripCriteriaProps = {
  proposal: ProposalWithTransport;
};

const getTripCriteria = ({ proposal }: TripCriteriaProps) => {
  const inboundTravelSection = proposal.transports?.inbound?.travelSections[0];
  return {
    departure: inboundTravelSection?.departure?.cityId,
    directFlights: false,
    flightClass: inboundTravelSection?.transport.class,
  };
};

export const AdditionalTransportAdvancedSearchForm = ({
  proposal,
}: AdditionalTransportAdvancedSearchFormProps) => {
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const router = useRouter();
  const {
    query: { departure_city: departureCity },
  } = router;
  const { trackClickEvent } = useTrackEvent();

  const handleSubmit = ({ directFlights, flightClass, departure }) => {
    const formattedTransportSearchCriteria = getTransportSearchCriteria({
      proposal,
      departureCity: queryParamAsString(departureCity),
    });
    const inbound = formattedTransportSearchCriteria.journeys[0];
    const outbound = formattedTransportSearchCriteria.journeys[1];
    const firstTravelSection = inbound?.travelSections[0];
    const otherTravelSections: Array<TransportSearchTravelSection> =
      inbound?.travelSections.slice(1) || [];
    const enrichedTravelSections: Array<TransportSearchTravelSection> = [
      {
        ...firstTravelSection,
        departure: {
          ...firstTravelSection.departure,
          location: { ...firstTravelSection.departure.location, id: departure },
        },
      },
      ...otherTravelSections,
    ];
    const enrichedInbound: TransportSearchJourney = {
      ...inbound,
      travelSections: enrichedTravelSections,
    };
    const transportSearchCriteria: TransportSearchCriteria = {
      ...formattedTransportSearchCriteria,
      journeys: [enrichedInbound, outbound],
      transportClass: flightClass,
      isDirectFlightOnly: directFlights,
      maxConnections: directFlights ? 0 : undefined,
    };
    dispatch(
      fetchAdditionalTransports({
        proposal,
        transportSearchCriteria,
        appContext,
      }),
    );
  };

  return (
    <Formik
      initialValues={getTripCriteria({
        proposal,
      })}
      onSubmit={handleSubmit}
    >
      {({ isValid }) => (
        <BucketFormikForm data-testid="AdditionalTransportAdvancedSearchForm">
          <AdditionalTransportAdvancedSearchFormFields proposal={proposal} />
          <div className="mt-20 flex items-center justify-start">
            <Button
              colorBg={COLORS.BLACK}
              colorBorder={COLORS.BLACK}
              color={COLORS.WHITE}
              data-testid="event-submit"
              isDisabled={!isValid}
              onClick={() =>
                trackClickEvent(TRACK_EVENT_NAMES.CLICK_TRANSPORT_LAYER, {
                  detail_click: 'flight_advanced_search',
                })
              }
              type="submit"
            >
              {t('remote.searchAction')}
            </Button>
          </div>
        </BucketFormikForm>
      )}
    </Formik>
  );
};
